import React, { useMemo, useState } from "react";
import {
  Pencil,
  ExternalLink,
  Link as LinkIcon,
  ShieldCheck,
  User2,
} from "lucide-react";
import Modal from "@/components/ui/Modal";
import { CustomButton } from "@/components/ui/Buttons";
import { useAppSelector } from "@/redux/hooks/hooks";
import { TUser } from "@/types/types";
import EditCreatorExtrasModal from "./modals/EditCreatorExtrasModal";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "@/components/ui/VideoPlayerV3";

/** Core setup icons (same as signup step) */
import DslrImage from "@/assets/images/auth/recording-cor-setup/dslr.png";
import IphoneImage from "@/assets/images/auth/recording-cor-setup/iphone.png";
import AndroidImage from "@/assets/images/auth/recording-cor-setup/android.png";
import TripodImage from "@/assets/images/auth/recording-cor-setup/tripod.png";
import MicrophoneImage from "@/assets/images/auth/recording-cor-setup/microphone.png";
import NoiseControlImage from "@/assets/images/auth/recording-cor-setup/noise-control-room.png";
import ArtificialLightingImage from "@/assets/images/auth/recording-cor-setup/artificial-light.png";
import AestheticImage from "@/assets/images/auth/recording-cor-setup/aesthetic-bg.png";
import GreenScreenImage from "@/assets/images/auth/recording-cor-setup/gree-screen.png";
import PlusImage from "@/assets/images/auth/recording-cor-setup/plus.png";

/** ✅ robust empty checker */
export const isEmptyValue = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim().length === 0;
  if (Array.isArray(v))
    return v.length === 0 || v.every((x) => isEmptyValue(x));
  if (v instanceof Date) return false;
  if (typeof v === "object")
    return Object.keys(v as Record<string, unknown>).length === 0;
  return false;
};

const safeUrl = (url?: string) => {
  const u = (url || "").trim();
  if (!u) return "";
  if (!/^https?:\/\//i.test(u)) return `https://${u}`;
  return u;
};

const cleanProfileLinks = (links: any[]) =>
  (links || []).filter(
    (x) =>
      (typeof x?.url === "string" && x.url.trim() !== "") ||
      (x?.followers !== null && x?.followers !== undefined),
  );

const Chip = ({ text }: { text: string }) => (
  <span className="inline-flex items-center text-[11px] px-2.5 py-1 rounded-full border border-[#E5E9EB] bg-[#F7F7F7] text-[#475467]">
    {text}
  </span>
);

const EmptyDash = () => <span className="text-[#98A2B3]">—</span>;

const FieldRow = ({
  label,
  children,
  hidden,
}: {
  label: string;
  children: React.ReactNode;
  hidden?: boolean;
}) => {
  if (hidden) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-6 py-3 border-b border-[#F2F4F7] last:border-b-0">
      <div className="text-sm font-medium text-[#344054]">{label}</div>
      <div className="text-sm text-[#475467]">{children}</div>
    </div>
  );
};

const SectionTitle = ({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}) => (
  <div className="flex items-start gap-2">
    <div className="mt-0.5 text-[#475467]">{icon}</div>
    <div>
      <h4 className="text-sm font-semibold text-[#101828]">{title}</h4>
      {subtitle ? (
        <p className="text-xs text-[#667085] mt-0.5">{subtitle}</p>
      ) : null}
    </div>
  </div>
);

/** Core setup mapping so it never “humanizes weirdly” */
const CORE_SETUP_META: Record<string, { label: string; icon: string }> = {
  dslr: { label: "DSLR Camera", icon: DslrImage },
  iphone: { label: "iPhone", icon: IphoneImage },
  androidPhone: { label: "Android Phone", icon: AndroidImage },
  tripod: { label: "Tripod / Stable mount", icon: TripodImage },
  microphone: { label: "External microphone", icon: MicrophoneImage },
  "noise-control": {
    label: "Quiet / noise-controlled room",
    icon: NoiseControlImage,
  },
  "artificial-lighting": {
    label: "Artificial lighting",
    icon: ArtificialLightingImage,
  },
  "green-screen": { label: "Green screen", icon: GreenScreenImage },
  "aesthetic-bg": { label: "Aesthetic background", icon: AestheticImage },
  others: { label: "Others", icon: PlusImage },
};

const CoreSetupChips = ({ items }: { items: any[] }) => {
  const list = Array.isArray(items) ? items : [];
  const filtered = list.filter((x) => !isEmptyValue(x?.type));

  if (filtered.length === 0) return <EmptyDash />;

  return (
    <div className="flex flex-wrap gap-2">
      {filtered.map((x) => {
        const meta = CORE_SETUP_META[String(x.type)] ?? {
          label: String(x.type),
          icon: PlusImage,
        };

        const details = String(x?.details || "").trim();

        return (
          <div
            key={String(x.type)}
            className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-xl border border-[#EEF2F6] bg-[#FCFCFD]"
          >
            <img
              src={meta.icon}
              alt={meta.label}
              className="size-4 opacity-80"
            />
            <span className="text-[12px] font-medium text-[#101828]">
              {meta.label}
            </span>
            {details ? (
              <span className="text-[11px] text-[#667085]">• {details}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const ProfileLinksList = ({ links }: { links: any[] }) => {
  const list = cleanProfileLinks(links || []);
  if (list.length === 0) return <EmptyDash />;

  return (
    <div className="space-y-2">
      {list.map((x) => {
        const url = safeUrl(x?.url);
        return (
          <div
            key={x?.id || `${x?.platform}-${url}`}
            className="flex items-center justify-between gap-3 rounded-xl border border-[#EEF2F6] bg-[#FCFCFD] px-4 py-3"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#101828]">
                  {String(x?.platform || "Link")}
                </span>
                {x?.followers !== null && x?.followers !== undefined ? (
                  <span className="text-xs px-2 py-0.5 rounded-full border border-[#E4E7EC] bg-white text-[#344054]">
                    {Number(x.followers).toLocaleString()} followers
                  </span>
                ) : null}
              </div>

              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-sm text-[#175CD3] hover:underline truncate"
                >
                  <LinkIcon className="size-4" />
                  <span className="truncate">{url}</span>
                  <ExternalLink className="size-4" />
                </a>
              ) : (
                <div className="mt-1 text-sm text-[#98A2B3]">No URL added</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PortfolioList = ({ items }: { items: any[] }) => {
  const list = (items || []).filter(
    (v: any) => !isEmptyValue(v?.description) || !isEmptyValue(v?.brandName),
  );
  if (list.length === 0) return <EmptyDash />;

  const [playingUrl, setPlayingUrl] = useState("");

  return (
    <div className="space-y-3">
      {list.map((v: any) => {
        const url =
          v.sourceType === "upload"
            ? (v.file?.live_url as string | undefined)
            : (v.linkUrl as string | undefined);

        const thumb =
          v.sourceType === "upload"
            ? ((v.file as any)?.thumbnail as string)
            : "";

        const hash =
          v.sourceType === "upload"
            ? ((v.file as any)?.hash as string) || DEFAULT_BLURHASH
            : DEFAULT_BLURHASH;

        const title = v.brandName?.trim()
          ? v.brandName
          : v.isPersonalProject
            ? "Personal Project"
            : "Untitled";

        return (
          <div
            key={v.id}
            className="rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] transition"
          >
            <div className="p-3 flex gap-3">
              <div className="w-[84px] h-[112px] rounded-xl overflow-hidden border border-[#EEF2F6] bg-[#F8FAFC] shrink-0">
                {v.sourceType === "upload" && url ? (
                  <VideoPlayerV3
                    url={url}
                    thumbnailUrl={thumb}
                    playing={playingUrl === url}
                    muted={false}
                    blurhash={hash}
                    onPlay={() =>
                      setPlayingUrl((cur) => (cur === url ? "" : url))
                    }
                    border="rounded-xl"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[10px] text-[#98A2B3]">
                    {v.sourceType === "link" ? "Link" : "No video"}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <h6 className="text-sm font-semibold text-[#101828] truncate">
                        {title}
                      </h6>

                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-[#E4E7EC] bg-[#FCFCFD] text-[#344054]">
                          {v.isPersonalProject ? "Personal" : "Client"}
                        </span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-[#FDE2D1] bg-[#FFF7ED] text-[#F97316]">
                          {v.sourceType === "upload" ? "Upload" : "Link"}
                        </span>
                      </div>
                    </div>

                    <p className="text-xs text-[#667085] mt-1">
                      {v.createdAt
                        ? new Date(v.createdAt).toLocaleDateString()
                        : "—"}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-[#475467] mt-2 line-clamp-2">
                  {v.description?.trim()
                    ? v.description
                    : "No description provided."}
                </p>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-[11px] text-[#98A2B3] truncate">
                    {v.sourceType === "link"
                      ? v.linkUrl || "No link"
                      : v.file?.live_url || "Not uploaded yet"}
                  </div>

                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#175CD3] hover:underline shrink-0"
                    >
                      View <ExternalLink className="size-4" />
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CreatorExtrasDetails = ({ data }: { data: TUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const userSlice = useAppSelector((s) => s.user.user);

  const u: any = userSlice ?? data ?? {};

  const showSection = useMemo(() => {
    return !isEmptyValue([
      u?.coreSetup,
      u?.canBringToVideo,
      u?.whoCanAppear,
      u?.avoidTopics,
      u?.portfolioVideos,
      u?.comfortableWith,
      u?.profileLinks,
      u?.languagesWithFluency,
    ]);
  }, [u]);

  const forceShow = true;
  if (!forceShow && !showSection) return null;

  const whoCanAppear = Array.isArray(u?.whoCanAppear) ? u.whoCanAppear : [];
  const avoidTopics = Array.isArray(u?.avoidTopics) ? u.avoidTopics : [];
  const comfortableWith =
    u?.comfortableWith && typeof u.comfortableWith === "object"
      ? u.comfortableWith
      : {};

  return (
    <>
      <div className="w-full sm:w-[69%] mx-auto lg:mx-0">
        {/* Match the SAME section heading style as others */}
        <div className="space-y-2.5 mb-8">
          <h5 className="text-lg leading-5 tracking-[-1%] font-semibold">
            Creator Setup & Preferences
          </h5>
          <p className="text-[#475467] leading-5">
            Your shooting setup, content boundaries, links, and portfolio.
          </p>
        </div>

        {/* Single big card like other sections */}
        <div className="border border-[#EAECF0] rounded-2xl bg-white">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#F2F4F7]">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#101828]">
                Setup Details
              </p>
              <p className="text-xs text-[#667085] mt-0.5">
                Keep this updated so brands can match you faster.
              </p>
            </div>

            <CustomButton
              onClick={() => setIsOpen(true)}
              variant="outline"
              color="#344054"
              fontSize="14px"
              padding="8px 10px"
              border="1px solid #E8EBEE"
              borderRadius="10px"
              className="flex items-center gap-2 shrink-0"
            >
              <Pencil className="size-4" />
              Edit
            </CustomButton>
          </div>

          <div className="px-5 py-5 space-y-6">
            {/* Appearances & Topics */}
            <div className="space-y-3 ">
              <SectionTitle
                icon={<User2 className="size-5" />}
                title="Appearances & Topics"
                subtitle="Who can appear and what topics to avoid."
              />

              <div className="border px-4 border-[#F2F4F7] rounded-xl">
                <FieldRow
                  label="Who can appear"
                  hidden={whoCanAppear.length === 0}
                >
                  <div className="flex flex-wrap gap-2">
                    {whoCanAppear.map((x: string) => (
                      <Chip key={x} text={x} />
                    ))}
                  </div>
                </FieldRow>

                <FieldRow
                  label="Avoid topics"
                  hidden={avoidTopics.length === 0}
                >
                  <div className="flex flex-wrap gap-2">
                    {avoidTopics.map((x: string) => (
                      <Chip key={x} text={x} />
                    ))}
                  </div>
                </FieldRow>

                <FieldRow
                  label="Can bring to video"
                  hidden={isEmptyValue(u?.canBringToVideo)}
                >
                  {String(u?.canBringToVideo || "") || <EmptyDash />}
                </FieldRow>

                {/* If all empty */}
                {whoCanAppear.length === 0 &&
                avoidTopics.length === 0 &&
                isEmptyValue(u?.canBringToVideo) ? (
                  <div className="px-4 py-3 text-sm text-[#98A2B3]">—</div>
                ) : null}
              </div>
            </div>

            {/* Comfortable With */}
            <div className="space-y-3">
              <SectionTitle
                icon={<ShieldCheck className="size-5" />}
                title="Comfortable With"
                subtitle="Your preferences for brand tasks and content options."
              />

              <div className="border border-[#F2F4F7] rounded-xl p-4">
                {isEmptyValue(comfortableWith) ? (
                  <EmptyDash />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(comfortableWith)
                      .filter(([, v]) => !isEmptyValue(v))
                      .map(([k, v]) => (
                        <div
                          key={k}
                          className="rounded-xl border border-[#EEF2F6] bg-[#FCFCFD] px-4 py-3"
                        >
                          <div className="text-xs font-medium text-[#667085]">
                            {k}
                          </div>
                          <div className="text-sm font-semibold text-[#101828] mt-0.5">
                            {String(v)}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Profile Links */}
            <div className="space-y-3">
              <SectionTitle
                icon={<LinkIcon className="size-5" />}
                title="Profile Links"
                subtitle="Social links and follower counts."
              />
              <ProfileLinksList links={u?.profileLinks || []} />
            </div>

            <div>
              <p className="text-xs font-medium text-[#344054] mb-2">
                Core setup
              </p>
              <CoreSetupChips items={u?.coreSetup || []} />
            </div>
            {/* Portfolio + Core Setup */}
            <div className="space-y-3">
              <SectionTitle
                icon={<span className="text-[#475467]">🎥</span>}
                title="Portfolio"
                subtitle="Your uploaded portfolio videos."
              />

              <div className="">
                <div className="pt-2">
                  <PortfolioList items={u?.portfolioVideos || []} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        preventCloseOnClickOutside={true}
        width="700px"
        isOpen={isOpen}
        setOpen={setIsOpen}
        padding="0px"
      >
        <EditCreatorExtrasModal data={data} onClose={setIsOpen} />
      </Modal>
    </>
  );
};

export default CreatorExtrasDetails;
